import type { HookFetchPlugin } from 'hook-fetch';
import FingerprintJS from '@fingerprintjs/fingerprintjs'; // 新增指纹库
import { ElMessage } from 'element-plus';
import hookFetch from 'hook-fetch';
import { sseTextDecoderPlugin } from 'hook-fetch/plugins';
import { ROUTER_WHITE_LIST } from '@/config';
import { isRoutePatternMatch } from '@/config/domainRouteAccess';
import router from '@/routers';
import { useUserStore } from '@/stores';

function isCurrentPageWhitelisted() {
  return ROUTER_WHITE_LIST.some(p => isRoutePatternMatch(p, window.location.pathname));
}
// 初始化指纹（单例模式）
const fpPromise = FingerprintJS.load();

// 获取浏览器指纹（缓存结果）
async function getFingerprint(): Promise<string> {
  const fp = await fpPromise;
  const { visitorId } = await fp.get();
  return visitorId;
}

// 标准响应格式
interface BaseResponse<T = any> {
  code: number;
  data: T;
  msg: string;
}

const SAFE_JSON_EXTRA_KEY = '__safeJsonFromText';
const EMPTY_RESPONSE_STATUS = new Set([204, 205]);

function isSuccessCode(code?: number) {
  return typeof code === 'number' && code >= 200 && code < 300;
}

function toSuccessResponse<T = any>(data: T = null as T): BaseResponse<T> {
  return {
    code: 200,
    data,
    msg: 'success',
  };
}

function withSafeJsonOptions<T extends { extra?: Record<string, any> }>(options?: T): T {
  return {
    ...(options ?? {}),
    extra: {
      ...(options?.extra ?? {}),
      [SAFE_JSON_EXTRA_KEY]: true,
    },
  } as unknown as T;
}

function withSafeJson<T extends { json: () => Promise<any>; text: () => Promise<any> }>(requestTask: T): T {
  requestTask.json = () => requestTask.text();
  return requestTask;
}

declare module 'hook-fetch' {
  interface HookFetchDefaults {
    response: any;
  }
}

export const request = hookFetch.create<BaseResponse>({
  baseURL: import.meta.env.VITE_WEB_BASE_API,
  headers: {
    'Content-Type': 'application/json',
  },
  plugins: [
    sseTextDecoderPlugin({ json: true, prefix: 'data:' }),
    {
      name: 'fingerprint-plugin', // 新增指纹插件
      beforeRequest: async (config) => {
        try {
          const fingerprint = await getFingerprint();
          config.headers = new Headers(config.headers);
          config.headers.set('X-Fingerprint', fingerprint);
        }
        catch (error) {
          console.error('Failed to generate fingerprint:', error);
        }
        return config;
      },
    },
    {
      name: 'safe-json-parser',
      priority: -20,
      afterResponse: async (response, config) => {
        if (!config.extra?.[SAFE_JSON_EXTRA_KEY] || response.responseType !== 'text') {
          return response;
        }

        const status = response.response.status;
        const rawResult = response.result;

        if (EMPTY_RESPONSE_STATUS.has(status) || rawResult === '' || rawResult == null) {
          return {
            ...response,
            result: toSuccessResponse(),
          };
        }

        if (typeof rawResult !== 'string') {
          return response;
        }

        return {
          ...response,
          result: JSON.parse(rawResult),
        };
      },
    },
    {
      name: 'adapt-array-response',
      afterResponse: async (response) => {
        if (EMPTY_RESPONSE_STATUS.has(response.response.status)) {
          return {
            ...response,
            result: toSuccessResponse(),
          };
        }

        if (typeof response.result?.code === 'number') {
          return response;
        }
        return {
          ...response,
          result: toSuccessResponse(response.result),
        };
      },
    },
  ],
});

// JWT插件（保持原有逻辑）
function jwtPlugin(): HookFetchPlugin {
  return {
    name: 'jwt',
    beforeRequest: async (config) => {
      // 延迟获取 store，确保 Pinia 已经初始化
      const userStore = useUserStore();
      config.headers = new Headers(config.headers);
      if (userStore.refreshToken) {
        config.headers.set('refresh_token', `${userStore.refreshToken}`);
      }
      if (userStore.token) {
        config.headers.set('authorization', `Bearer ${userStore.token}`);
      }

      return config;
    },

    // 响应后处理
    afterResponse: async (response: any) => {
      // 延迟获取 store，确保 Pinia 已经初始化
      const userStore = useUserStore();
      if (response.response.headers.get('access_token')) {
        userStore.setToken(response.response.headers.get('access_token'), response.response.headers.get('refresh_token'));
      }

      if (isSuccessCode(response.result?.code))
        return response;

      if (response.result?.code === 403) {
        router.replace({ name: '403' });
        ElMessage.error(response.result?.msg);
        return Promise.reject(response);
      }

      if (response.result?.code === 401) {
        if (!isCurrentPageWhitelisted()) {
          ElMessage.error('登录已过期，请重新登录');
          userStore.logout();
          router.replace('/login');
        }
      }

      ElMessage.error(response.result?.msg);
      return Promise.reject(response);
    },

    onError: async (error) => {
      // 延迟获取 store，确保 Pinia 已经初始化
      const userStore = useUserStore();
      if (error.status === 403 && error.response) {
        const data = await (error.response.json());
        const msg = data?.error?.message;
        // 有业务 message 的 403（如"已有套餐"）透传给调用方处理，不弹全局 toast
        // 已经有codingplan的时候，返回了422，单独处理
        if (data?.error?.code === '422') {
          return Promise.reject(data?.error);
        }

        if (msg) {
          ElMessage.error(msg || '无权限');
          return Promise.reject(data?.error);
        }
        return Promise.reject(new Error(msg || '无权限'));
      }

      if (error.status === 401) {
        if (!isCurrentPageWhitelisted()) {
          ElMessage.error('登录已过期，请重新登录！');
          userStore.logout();
          router.replace('/login');
        }
      }
    },
  };
}

request.use(jwtPlugin());

// 导出方法（保持原有）
export function post<T = any, D = any, P = any>(url: string, data?: D, options?: any) {
  return withSafeJson(request.post<T, D, P>(url, data, withSafeJsonOptions(options)));
}

export function get<T = any, P = any>(url: string, params?: P, options?: any) {
  return withSafeJson(request.get<T, P>(url, params, withSafeJsonOptions(options)));
}

export function put<T = any, D = any, P = any>(url: string, data?: D, options?: any) {
  return withSafeJson(request.put<T, D, P>(url, data, withSafeJsonOptions(options)));
}

export function del<T = any, P = any>(url: string, options?: any) {
  return withSafeJson(request.delete<T, P>(url, withSafeJsonOptions(options)));
}
export default request;
